import { Injectable, SimpleChanges } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { CustomValidator } from '@helpers/custom-validator';
import { enumChargesDistributionType, enumTransportationMode } from '@data-layer/enums/entry-summaries';
import { EntrySummaryModel } from '@data-layer/models/entry-summaries';
import { debounceTime, take } from 'rxjs/operators';
import { ShipmentModel } from '@data-layer/models/common';
import { EntrySummaryStateService } from './entry-summary-state.service';
import { FormHelper } from '@helpers/form.helper';
import { IFieldTypeConfig } from '@interfaces/field-type-config';
import { enumFormControlType } from '@enum/enumFormControlType';
import { EntityModel } from '@data-layer/models/entities';
import { UtilsHelper } from '@helpers/utils-helper';
import { IBillOfLadingFormData } from '@app/modules/entry-summary/interfaces';
import { INPUT_TYPES_MAP } from '@constants/input-types-map';
import { EntrySummaryActionsHelper } from '@helpers/entry-summary-actions.helper';

@Injectable({ providedIn: 'root' })
export class EsCreateBlFormService {
  private formGroup: UntypedFormGroup;
  private entrySummary: EntrySummaryModel;
  private currentShipment: ShipmentModel;
  private isCanEdit: boolean;
  private isAir: boolean = false;
  private isRail: boolean = false;

  constructor(private fb: UntypedFormBuilder, private fh: FormHelper, private entrySummaryStateService: EntrySummaryStateService) {}

  public createForm(withFreightCost: boolean = true): UntypedFormGroup {
    this.createBLForm(withFreightCost);

    return this.formGroup;
  }

  public setData(entrySummary: EntrySummaryModel, currentShipment: ShipmentModel, isCanEdit: boolean): void {
    this.entrySummary = entrySummary;
    this.currentShipment = currentShipment;
    this.isCanEdit = isCanEdit;
    this.isAir = entrySummary.transportationMode === enumTransportationMode.AIR;
    this.isRail = entrySummary.transportationMode === enumTransportationMode.RAIL;
  }

  public updateShipmentForm(entrySummary: EntrySummaryModel, isValidateAfterInit: boolean, seller?: EntityModel): void {
    if (this.isCanEdit) {
      this.formGroup.enable();
    } else {
      this.formGroup.disable();
    }
    const { hasSingleExportData, foreignPortOfLadingId, exportDate } =
      this.entrySummaryStateService.getSummaryForeignPortOfLadingData(entrySummary);
    const initialDataToUpdate = {
      foreignPortOfLadingId: !hasSingleExportData && foreignPortOfLadingId ? foreignPortOfLadingId : null,
      exportDate: !hasSingleExportData && exportDate ? exportDate : null,
    };
    if (this.currentShipment) {
      const dataToUpdate = {
        masterBill: this.currentShipment.masterBill,
        houseBill: this.currentShipment.houseBill,
        foreignPortOfLadingId: !initialDataToUpdate.foreignPortOfLadingId
          ? this.currentShipment.foreignPortOfLadingId
          : initialDataToUpdate.foreignPortOfLadingId,
        exportDate: !initialDataToUpdate.exportDate
          ? this.currentShipment.exportDate
            ? this.currentShipment.exportDate
            : null
          : initialDataToUpdate.exportDate,
        itDate: this.currentShipment.itDate,
        itNumber: this.currentShipment.itNumber,
        insurance: this.currentShipment.insurance,
        transportationCharge: this.currentShipment.transportationCharge,
        freightCost: this.currentShipment.freightCost,
        chargesDistributionType: this.currentShipment.chargesDistributionType,
        quantity: this.currentShipment.quantity,
        quantityUnit: this.currentShipment.quantityUnit,
        ...(seller && { seller: seller }),
      };
      for (const prop in dataToUpdate) {
        if (!dataToUpdate[prop]) {
          delete dataToUpdate[prop];
        }
      }

      this.formGroup.patchValue(dataToUpdate);
    } else {
      for (const prop in initialDataToUpdate) {
        if (!initialDataToUpdate[prop]) {
          delete initialDataToUpdate[prop];
        }
      }
      this.formGroup.patchValue(initialDataToUpdate);
    }

    if ((!hasSingleExportData && !foreignPortOfLadingId) || this.isAir) {
      this.formGroup.get('foreignPortOfLadingId').disable();
    } else {
      this.formGroup.get('foreignPortOfLadingId').enable();
    }

    if (!hasSingleExportData && !exportDate) {
      this.formGroup.get('exportDate').disable();
    } else {
      this.formGroup.get('exportDate').enable();
    }

    this.updateValidationForBLNumbers(entrySummary);

    const { portOfEntryCode, portOfUnladingCode } = this.entrySummary;
    const isImmediateTransfer = portOfEntryCode !== portOfUnladingCode || !portOfEntryCode || !portOfUnladingCode;
    if (isImmediateTransfer) {
      this.formGroup.get('itDate').setValidators([Validators.required]);
      this.formGroup.get('itNumber').setValidators([Validators.required, Validators.minLength(9), Validators.maxLength(11)]);
    } else {
      this.formGroup.get('itDate').clearValidators();
      this.formGroup.get('itNumber').clearValidators();
    }

    if (isValidateAfterInit) {
      this.formGroup.updateValueAndValidity({ onlySelf: false });
      this.fh.markAsTouched(this.formGroup);
      this.formGroup.statusChanges.pipe(debounceTime(300), take(1)).subscribe(() => {
        this.focusFirstInvalidControl();
      });
    }

    if (entrySummary.transportationMode === enumTransportationMode.TRUCK) {
      this.formGroup.get(['masterBill', 'number']).clearValidators();
    }
  }

  public setFormTouched(): void {
    this.formGroup.markAllAsTouched();
  }

  public labelsData(isDesktop: boolean): { [key: string]: IBillOfLadingFormData } {
    return {
      [enumTransportationMode.OCEAN]: {
        title: 'ENTRY_SUMMARY.BILL_OF_LADING',
        subTitle: 'INFO.BL.SUB_TITLE_OCEAN',
        carrier: {
          titleMaster: 'FORM_LABEL.MASTER_CARRIER',
          titleHouse: 'FORM_LABEL.HOUSE_CARRIER',
          placeholder: 'FORM_PLACEHOLDER.CARRIER',
          helper: isDesktop ? 'FORM_HELPER.CARRIER_DESKTOP' : 'FORM_HELPER.CARRIER',
          maskMaster: 'S{4}',
          maskHouse: 'S{4}',
          inputmodeMaster: 'text',
          inputmodeHouse: 'text',
          patternMaster: '',
          patternHouse: '',
          maskMessageMaster: 'MASK_ERROR.ONLY_ENGLISH',
          maskMessageHouse: 'MASK_ERROR.ONLY_ENGLISH',
          maxMaskLength: 4,
        },
        number: {
          titleMaster: 'FORM_LABEL.MASTER_BL',
          titleHouse: 'FORM_LABEL.HOUSE_BL',
          placeholder: 'FORM_PLACEHOLDER.BILL_NUMBER',
          helper: isDesktop ? 'FORM_HELPER.BILL_NUMBER_DESKTOP' : 'FORM_HELPER.BILL_NUMBER',
          maskMaster: 'A{12}',
          maskHouse: 'A{12}',
          inputmodeMaster: 'text',
          inputmodeHouse: 'text',
          patternMaster: '',
          patternHouse: '',
          maskMessageMaster: 'MASK_ERROR.ONLY_ENGLISH_DIGITS',
          maskMessageHouse: 'MASK_ERROR.ONLY_ENGLISH_DIGITS',
          maxMaskLength: 12,
          ...(isDesktop && { tooltip: 'INFO.BL.SUB_TITLE_BL_OCEAN_DESKTOP' }),
        },
        packages: {
          title: 'INFO.BL.TITLE_PACKAGES',
        },
      },
      [enumTransportationMode.AIR]: {
        title: 'FORM_LABEL.AIRWAY_BILL',
        subTitle: 'INFO.BL.SUB_TITLE_AIR',
        carrier: {
          titleMaster: 'FORM_LABEL.MASTER_AIRWAY_BILL_PREFIX',
          titleHouse: 'FORM_LABEL.HOUSE_AIRWAY_BILL_PREFIX',
          placeholder: 'FORM_PLACEHOLDER.DIGITS_AIRLINE_PREFIX',
          helper: 'FORM_PLACEHOLDER.DIGITS_AIRLINE_PREFIX',
          maskMaster: '0{3}',
          maskHouse: 'A{3}',
          inputmodeMaster: 'decimal',
          inputmodeHouse: 'text',
          patternMaster: 'INPUT_TYPES_MAP.INTEGER.pattern',
          patternHouse: '',
          maskMessageMaster: 'MASK_ERROR.ONLY_DIGITS',
          maskMessageHouse: 'MASK_ERROR.ONLY_ENGLISH_DIGITS',
          maxMaskLength: 3,
          ...(isDesktop && { tooltip: 'INFO.BL.SUB_TITLE_SCAC_AIR_DESKTOP' }),
        },
        number: {
          titleMaster: 'FORM_LABEL.MASTER_AIRWAY_BILL_NUMBER',
          titleHouse: 'FORM_LABEL.HOUSE_AIRWAY_BILL_NUMBER',
          placeholder: 'FORM_PLACEHOLDER.DIGITS_AIRLINE_NUMBER',
          helper: 'FORM_PLACEHOLDER.DIGITS_AIRLINE_NUMBER',
          maskMaster: '0{9}',
          maskHouse: 'A{9}',
          inputmodeMaster: 'decimal',
          inputmodeHouse: 'text',
          patternMaster: 'INPUT_TYPES_MAP.INTEGER.pattern',
          patternHouse: '',
          maskMessageMaster: 'MASK_ERROR.ONLY_DIGITS',
          maskMessageHouse: 'MASK_ERROR.ONLY_ENGLISH_DIGITS',
          maxMaskLength: 9,
          ...(isDesktop && { tooltip: 'INFO.BL.SUB_TITLE_BL_AIR_DESKTOP' }),
        },
        packages: {
          title: 'INFO.BL.TITLE_PACKAGES',
        },
      },
      [enumTransportationMode.RAIL]: {
        title: 'ENTRY_SUMMARY.BILL_OF_LADING',
        subTitle: 'INFO.BL.SUB_TITLE_RAIL',
        carrier: {
          titleMaster: 'FORM_LABEL.MASTER_CARRIER',
          titleHouse: '',
          placeholder: 'FORM_PLACEHOLDER.CARRIER',
          helper: isDesktop ? 'FORM_HELPER.CARRIER_DESKTOP' : 'FORM_HELPER.CARRIER',
          maskMaster: 'S{4}',
          maskHouse: 'S{4}',
          inputmodeMaster: 'text',
          inputmodeHouse: 'text',
          patternMaster: '',
          patternHouse: '',
          maskMessageMaster: 'MASK_ERROR.ONLY_ENGLISH',
          maskMessageHouse: 'MASK_ERROR.ONLY_ENGLISH',
          maxMaskLength: 4,
          ...(isDesktop && { tooltip: 'INFO.BL.SUB_TITLE_SCAC_RAIL_DESKTOP' }),
        },
        number: {
          titleMaster: 'FORM_LABEL.MASTER_BL',
          titleHouse: '',
          placeholder: 'FORM_PLACEHOLDER.BILL_NUMBER',
          helper: isDesktop ? 'FORM_HELPER.BILL_NUMBER_DESKTOP' : 'FORM_HELPER.BILL_NUMBER',
          maskMaster: 'A{12}',
          maskHouse: 'A{12}',
          inputmodeMaster: 'text',
          inputmodeHouse: 'text',
          patternMaster: '',
          patternHouse: '',
          maskMessageMaster: 'MASK_ERROR.ONLY_ENGLISH_DIGITS',
          maskMessageHouse: 'MASK_ERROR.ONLY_ENGLISH_DIGITS',
          maxMaskLength: 12,
          ...(isDesktop && { tooltip: 'INFO.BL.SUB_TITLE_BL_OCEAN_DESKTOP' }),
        },
        packages: {
          title: 'INFO.BL.TITLE_PACKAGES',
        },
      },
      default: {
        title: 'ENTRY_SUMMARY.BILL_OF_LADING',
        subTitle: 'INFO.BL.SUB_TITLE_OCEAN',
        carrier: {
          titleMaster: 'FORM_LABEL.MASTER_CARRIER',
          titleHouse: 'FORM_LABEL.HOUSE_CARRIER',
          placeholder: 'FORM_PLACEHOLDER.CARRIER',
          helper: isDesktop ? 'FORM_HELPER.CARRIER_DESKTOP' : 'FORM_HELPER.CARRIER',
          maskMaster: 'S{4}',
          maskHouse: 'S{4}',
          inputmodeMaster: 'text',
          inputmodeHouse: 'text',
          patternMaster: '',
          patternHouse: '',
          maskMessageMaster: 'MASK_ERROR.ONLY_ENGLISH',
          maskMessageHouse: 'MASK_ERROR.ONLY_ENGLISH',
          maxMaskLength: 4,
          ...(isDesktop && { tooltip: 'INFO.BL.SUB_TITLE_SCAC_OCEAN_DESKTOP' }),
        },
        number: {
          titleMaster: 'FORM_LABEL.MASTER_BL',
          titleHouse: 'FORM_LABEL.HOUSE_BL',
          placeholder: 'FORM_PLACEHOLDER.BILL_NUMBER',
          helper: isDesktop ? 'FORM_HELPER.BILL_NUMBER_DESKTOP' : 'FORM_HELPER.BILL_NUMBER',
          maskMaster: 'A{12}',
          maskHouse: 'A{12}',
          inputmodeMaster: 'text',
          inputmodeHouse: 'text',
          patternMaster: '',
          patternHouse: '',
          maskMessageMaster: 'MASK_ERROR.ONLY_ENGLISH_DIGITS',
          maskMessageHouse: 'MASK_ERROR.ONLY_ENGLISH_DIGITS',
          maxMaskLength: 12,
          ...(isDesktop && { tooltip: 'INFO.BL.SUB_TITLE_BL_OCEAN_DESKTOP' }),
        },
        packages: {
          title: 'INFO.BL.TITLE_PACKAGES',
        },
      },
    };
  }

  public errorsData(): { [key: string]: any } {
    return {
      code: {
        required: 'FORM_ERROR.REQUIRED',
      },
      masterBillCarrier: {
        required: 'FORM_ERROR.REQUIRED',
        wrongReferenceNumberShip: 'FORM_ERROR.WRONG_REFERENCE_NUMBER_SHIP',
        sameAsHouseBillNumber: 'FORM_ERROR.SAME_BL_OCEAN',
        shipmentHBLMatch: 'FORM_ERROR.SHIPMENT_MBL_MATCH_TO_HBL',
        shipmentBLMatchSCAC: 'FORM_ERROR.SHIPMENT_BL_MATCH_SCAC',
        minlength: 'FORM_ERROR.MIN_LENGTH',
        maxlength: 'FORM_ERROR.MAX_LENGTH',
      },
      masterBillNumber: {
        required: 'FORM_ERROR.REQUIRED',
        wrongReferenceNumberShip: 'FORM_ERROR.WRONG_REFERENCE_NUMBER_SHIP',
        sameAsHouseBillNumber: 'FORM_ERROR.SAME_BL_OCEAN',
        shipmentHBLMatch: 'FORM_ERROR.SHIPMENT_MBL_MATCH_TO_HBL',
        shipmentBLMatchSCAC: 'FORM_ERROR.SHIPMENT_BL_MATCH_SCAC',
        minlength: 'FORM_ERROR.MIN_LENGTH',
        maxlength: 'FORM_ERROR.MAX_LENGTH',
      },
      houseBillCarrier: {
        required: 'FORM_ERROR.REQUIRED',
        wrongReferenceNumberShip: 'FORM_ERROR.WRONG_REFERENCE_NUMBER_SHIP',
        sameAsMasterBillNumber: 'FORM_ERROR.SAME_BL_OCEAN',
        shipmentBLMatch: 'FORM_ERROR.SHIPMENT_HBL_NOT_UNIQUE',
        minlength: 'FORM_ERROR.MIN_LENGTH',
        maxlength: 'FORM_ERROR.MAX_LENGTH',
      },
      houseBillNumber: {
        required: 'FORM_ERROR.REQUIRED',
        wrongReferenceNumberShip: 'FORM_ERROR.WRONG_REFERENCE_NUMBER_SHIP',
        sameAsMasterBillNumber: 'FORM_ERROR.SAME_BL_OCEAN',
        shipmentBLMatch: 'FORM_ERROR.SHIPMENT_HBL_NOT_UNIQUE',
        minlength: 'FORM_ERROR.MIN_LENGTH',
        maxlength: 'FORM_ERROR.MAX_LENGTH',
      },
      required: 'FORM_ERROR.REQUIRED',
    };
  }

  public ctxOnChanges(changes: SimpleChanges, thisCtx: any /* component this context */): void {
    if (changes.entrySummary) {
      thisCtx.isCanEdit = EntrySummaryActionsHelper.isCanEdit(this.entrySummary);
      thisCtx.isCanRemoveDocuments = EntrySummaryActionsHelper.isCanRemoveDocuments(this.entrySummary);
      thisCtx.isCustomBroker = thisCtx.entrySummaryStateService.isShowDocuments(this.entrySummary);
      thisCtx.isShowDocuments = thisCtx.isCustomBroker ? true : thisCtx.isShowDocuments;
      thisCtx.isAir = thisCtx.entrySummary.transportationMode === enumTransportationMode.AIR;
      thisCtx.isRail = thisCtx.entrySummary.transportationMode === enumTransportationMode.RAIL;
      thisCtx.isTruck = thisCtx.entrySummary.transportationMode === enumTransportationMode.TRUCK;
      this.onUpdateEntrySummary(thisCtx);
      if (thisCtx.entrySummary.transportationMode) {
        thisCtx.billOfLadingData = thisCtx.data[thisCtx.entrySummary.transportationMode] || thisCtx.data.default;
      } else {
        thisCtx.billOfLadingData = thisCtx.data[enumTransportationMode.OCEAN];
      }
      thisCtx.updateErrorsCode(this.entrySummary.transportationMode);
    }
  }

  private onUpdateEntrySummary(thisCtx: any /* component this context */): void {
    thisCtx.exportMaxDate = thisCtx.entrySummary.arrivalDate;

    const { portOfEntryCode, portOfUnladingCode } = thisCtx.entrySummary;
    thisCtx.hasSingleExportData = thisCtx.entrySummary.hasSingleExportData;
    thisCtx.isImmediateTransfer = !thisCtx.isTruck
      ? portOfEntryCode !== portOfUnladingCode || !portOfEntryCode || !portOfUnladingCode
      : thisCtx.isWithVia;
    if (thisCtx.hasSingleExportData && !thisCtx.isAir && thisCtx.formGroup.get('foreignPortOfLadingId').value) {
      thisCtx.getCurrentForeignPort();
    }
  }

  private createBLForm(withFreightCost: boolean): void {
    this.formGroup = this.fb.group({
      masterBill: this.fb.group({
        carrier: [null, [Validators.required]],
        number: [null, [Validators.required, CustomValidator.shouldNotMatchBillNumber(['houseBill', 'number'], 'sameAsHouseBillNumber')]],
      }),
      houseBill: this.fb.group({
        carrier: [null, [Validators.required]],
        number: [null, [Validators.required, CustomValidator.shouldNotMatchBillNumber(['masterBill', 'number'], 'sameAsMasterBillNumber')]],
      }),
      foreignPortOfLadingId: [null, [Validators.required]],
      exportDate: [null, [Validators.required]],
      itDate: [null],
      itNumber: [null],
      insurance: new UntypedFormControl(null, CustomValidator.updateControl('freightCost')),
      transportationCharge: new UntypedFormControl(null, CustomValidator.updateControl('freightCost')),
      ...(withFreightCost && { freightCost: new UntypedFormControl(null, [Validators.required]) }),
      chargesDistributionType: new UntypedFormControl(enumChargesDistributionType.PREPAID, CustomValidator.updateControl('freightCost')),
      quantity: new UntypedFormControl(null, [Validators.required]),
      quantityUnit: new UntypedFormControl(null, [Validators.required]),
    });
  }

  private updateValidationForBLNumbers(entrySummary: EntrySummaryModel) {
    const { mblNumberValidators, mblCarrierValidators, hblNumberValidators, hblCarrierValidators } = this.getBLValidators(entrySummary);

    this.formGroup.get(['masterBill', 'number']).setValidators(mblNumberValidators);
    this.formGroup.get(['masterBill', 'carrier']).setValidators(mblCarrierValidators);
    this.formGroup.get(['houseBill', 'number']).setValidators(hblNumberValidators);
    this.formGroup.get(['houseBill', 'carrier']).setValidators(hblCarrierValidators);
  }

  private getBLValidators(entrySummary: EntrySummaryModel) {
    const blNumbers = this.getAllShipmentsBLNumbers(entrySummary.shipments || [], this.currentShipment ? this.currentShipment.id : null);
    const hblNumbers = this.getAllShipmentsBLNumbers(
      entrySummary.shipments || [],
      this.currentShipment ? this.currentShipment.id : null,
      true,
    );

    const blMasterSCAC = this.getShipmentBLMasterSCAC(entrySummary.shipments || [], this.currentShipment ? this.currentShipment.id : null);

    const mblCarrierValidators = [
      Validators.required,
      this.isAir ? Validators.maxLength(3) : Validators.maxLength(4),
      this.isAir ? Validators.minLength(3) : Validators.minLength(4),
    ];
    const mblNumberValidators = [
      Validators.required,
      CustomValidator.shouldNotMatchBillNumber(['houseBill', 'number'], 'sameAsHouseBillNumber'),
      this.isAir ? Validators.maxLength(9) : Validators.maxLength(12),
      Validators.minLength(1),
    ];
    if (hblNumbers && hblNumbers.length > 0) {
      mblNumberValidators.push(CustomValidator.notMatchValues(hblNumbers, 'shipmentHBLMatch'));
    }
    if (blMasterSCAC && blMasterSCAC.length > 0) {
      mblCarrierValidators.push(CustomValidator.shouldMatchSCACValue(blMasterSCAC));
      this.patchSCACToMBL(blMasterSCAC);
    }
    const hblCarrierValidators = [
      CustomValidator.shouldBLBeRequiredNumber(['houseBill', 'number'], this.isRail, 'required'),
      this.isAir ? Validators.maxLength(3) : Validators.maxLength(4),
      this.isAir ? Validators.minLength(3) : Validators.minLength(4),
    ];
    const hblNumberValidators = [
      CustomValidator.shouldBLBeRequiredNumber(['houseBill', 'carrier'], this.isRail, 'required'),
      CustomValidator.shouldNotMatchBillNumber(['masterBill', 'number'], 'sameAsMasterBillNumber'),
      this.isAir ? Validators.maxLength(9) : Validators.maxLength(12),
      Validators.minLength(1),
    ];
    if (blNumbers && blNumbers.length > 0) {
      hblNumberValidators.push(CustomValidator.notMatchValues(blNumbers, 'shipmentBLMatch'));
    }

    return { mblNumberValidators, mblCarrierValidators, hblNumberValidators, hblCarrierValidators };
  }

  private getAllShipmentsBLNumbers(shipments: ShipmentModel[], shipmentId?: number, onlyHBL: boolean = false): string[] {
    const result: string[] = [];
    for (const shipment of shipments) {
      if (shipment && (!shipmentId || shipment.id !== shipmentId)) {
        if (shipment.houseBill) {
          result.push(shipment.houseBill.number);
        }
        if (shipment.masterBill && !onlyHBL) {
          result.push(shipment.masterBill.number);
        }
      }
    }
    return UtilsHelper.uniq<string>(result);
  }

  private getShipmentBLMasterSCAC(shipments: ShipmentModel[], shipmentId?: number): string {
    for (const shipment of shipments) {
      if (shipment && (!shipmentId || shipment.id !== shipmentId)) {
        if (shipment.masterBill && shipment.masterBill.carrier) {
          return shipment.masterBill.carrier.toUpperCase();
        }
      }
    }
    return '';
  }

  private focusFirstInvalidControl() {
    this.fh.focusFirstInvalidControl(this.formGroup, this.fieldTypeConfig);
  }

  private patchSCACToMBL(scac: string) {
    if (!this.formGroup.get(['masterBill', 'carrier']).value) {
      this.formGroup.get(['masterBill', 'carrier']).patchValue(scac);
    }
  }

  private get fieldTypeConfig(): IFieldTypeConfig[] {
    return [
      {
        controlName: ['masterBill', 'carrier'],
        controlId: 'masterBillCarrier',
        type: enumFormControlType.INPUT,
      },
      {
        controlName: ['masterBill', 'number'],
        controlId: 'masterBillNumber',
        type: enumFormControlType.INPUT,
      },
      {
        controlName: ['houseBill', 'carrier'],
        controlId: 'houseBillCarrier',
        type: enumFormControlType.INPUT,
      },
      {
        controlName: ['houseBill', 'number'],
        controlId: 'houseBillNumber',
        type: enumFormControlType.INPUT,
      },
      {
        controlName: 'foreignPortOfLadingId',
        controlId: 'foreignPortOfLadingId',
        type: enumFormControlType.SELECT,
      },
      {
        controlName: 'exportDate',
        controlId: 'exportDate',
        type: enumFormControlType.DATE_PICKER,
      },
      {
        controlName: 'quantity',
        controlId: 'quantity',
        type: enumFormControlType.INPUT,
      },
      {
        controlName: 'quantityUnit',
        controlId: 'unitOfMeasure',
        type: enumFormControlType.SELECT,
      },
      {
        controlName: 'itNumber',
        controlId: 'itNumber',
        type: enumFormControlType.INPUT,
      },
      {
        controlName: 'itDate',
        controlId: 'itDate',
        type: enumFormControlType.DATE_PICKER,
      },
      {
        controlName: 'insurance',
        controlId: 'insurance',
        type: enumFormControlType.INPUT,
      },
      {
        controlName: 'transportationCharge',
        controlId: ['sm-transportationCharge', 'transportationCharge'],
        type: enumFormControlType.INPUT,
      },
      {
        controlName: 'freightCost',
        controlId: ['freightCost', 'charges'],
        type: enumFormControlType.INPUT,
      },
    ];
  }
}
